import { useEffect, useState } from "react";
import { Package, RefreshCw, TrendingUp } from "lucide-react";
import { Button } from "@/components/ui/button";
import API from "@/lib/api";

/**
 * Bundle Size Report — admin QoL card. Renders the total production JS
 * payload and the 5 heaviest chunks so we catch bundle bloat early. In
 * dev preview (no /app/frontend/build), shows an informative placeholder
 * instead of an error.
 */
export default function BundleSizeReportCard() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(true);

  const load = async () => {
    setBusy(true);
    setErr(null);
    try {
      const { data } = await API.get("/admin/bundle-report");
      setData(data);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Failed to load bundle report");
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => { load(); }, []);

  const totalKb = data?.total_kb;
  const chunks = data?.top_chunks || [];
  const okBudget = 2500;  // arbitrary "should be under this" mark
  const overBudget = typeof totalKb === "number" && totalKb > okBudget;

  return (
    <section className="bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800 p-5" data-testid="bundle-report-card">
      <div className="flex items-start gap-3 mb-4">
        <div className="w-10 h-10 rounded-lg bg-purple-100 dark:bg-purple-900/40 flex items-center justify-center flex-shrink-0">
          <Package className="w-5 h-5 text-purple-600 dark:text-purple-300" />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="text-base font-semibold text-slate-900 dark:text-slate-100">JS Bundle Size</h3>
          <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">Production build — top 5 heaviest chunks. Catch bloat before it ships.</p>
        </div>
        <button
          type="button"
          onClick={load}
          disabled={busy}
          className="p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-500 disabled:opacity-50"
          title="Refresh"
          data-testid="bundle-report-refresh"
        >
          <RefreshCw className={`w-4 h-4 ${busy ? "animate-spin" : ""}`} />
        </button>
      </div>

      {err ? (
        <div className="text-sm text-rose-600 dark:text-rose-400" data-testid="bundle-report-err">{err}</div>
      ) : busy ? (
        <div className="text-sm text-slate-500 dark:text-slate-400">Loading bundle report…</div>
      ) : data?.ok === false ? (
        <div className="text-sm text-slate-500 dark:text-slate-400 bg-slate-50 dark:bg-slate-800/60 border border-dashed border-slate-200 dark:border-slate-700 rounded-lg p-3">
          {data.message}
        </div>
      ) : (
        <>
          <div className="flex items-center gap-4 mb-4">
            <div>
              <div className="text-3xl font-black text-slate-900 dark:text-slate-100 tabular-nums" data-testid="bundle-report-total-kb">
                {totalKb ? `${totalKb.toFixed(1)}` : "—"}
                <span className="text-base font-semibold text-slate-500 ml-1">KB</span>
              </div>
              <div className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                {data?.file_count || 0} JS chunks
              </div>
            </div>
            {typeof totalKb === "number" ? (
              <div className={`ml-auto text-xs font-semibold px-2.5 py-1 rounded-full inline-flex items-center gap-1 ${overBudget ? "bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-300" : "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300"}`}>
                <TrendingUp className="w-3 h-3" />
                {overBudget ? `Over ${okBudget}KB budget` : `Under ${okBudget}KB budget`}
              </div>
            ) : null}
          </div>
          <ul className="space-y-1.5">
            {chunks.map((c, i) => (
              <li key={c.name} className="flex items-center gap-3 text-sm font-mono" data-testid={`bundle-report-chunk-${i}`}>
                <span className="text-slate-400 tabular-nums w-6">{i + 1}.</span>
                <span className="truncate flex-1 text-slate-700 dark:text-slate-300" title={c.name}>{c.name}</span>
                <span className="text-slate-500 tabular-nums flex-shrink-0">{c.kb.toFixed(1)} KB</span>
              </li>
            ))}
            {chunks.length === 0 ? (
              <li className="text-sm text-slate-500 italic">No JS chunks found — build folder may be empty.</li>
            ) : null}
          </ul>
        </>
      )}
    </section>
  );
}
